import { apiDomain, websiteID, bt_env, Settings } from "../../settings";
import { getPremiumDetectionData } from "../data";
import { verifySignature, decodeBase64 } from "./verifySignature";
import { RL_PREVIEW } from "./queryParams";
import {
  checkExtensionApiAvailable,
  getDetectedExtension,
} from "./extensionApi";

export interface AllowlistState {
  status: boolean;
  source: string | null;
  oneCA: boolean;
  aa?: boolean;
  expiresAt?: number;
}
export interface PremiumDetectionResult {
  isPremium: boolean;
  extensionInfo: {
    name: string;
    version: string;
    allowlistState: AllowlistState;
  } | null;
}

class PremiumABUserDetector {
  private singletonDetection?: Promise<PremiumDetectionResult>;
  private readonly premiumBypassModeEnabled: boolean;
  private RLEnabled: boolean;

  constructor(settings: Settings) {
    this.RLEnabled = settings.enabled;
    this.premiumBypassModeEnabled = settings.premium_bypass_mode_enabled;
    this.singletonDetection = this.detectPremiumABUser();
  }

  /**
   * Main detection method that determines whether to use the extension API
   * or legacy detection method based on availability
   */
  public async detectPremiumABUser(): Promise<PremiumDetectionResult> {
    if (this.singletonDetection !== undefined) {
      return this.singletonDetection;
    }

    if (RL_PREVIEW || !this.RLEnabled) {
      this.singletonDetection = Promise.resolve({
        isPremium: false,
        extensionInfo: null,
      });
      return this.singletonDetection;
    }

    const extensionApiAvailable = await checkExtensionApiAvailable();
    this.singletonDetection = extensionApiAvailable
      ? this.detectPremiumABUserViaExtensionApi()
      : this.detectPremiumABUserLegacy();

    return this.singletonDetection;
  }

  /**
   * Uses the extension API to detect premium status and gather extension info
   * Validates premium status through license signature verification
   */
  private async detectPremiumABUserViaExtensionApi(): Promise<PremiumDetectionResult> {
    try {
      const { extensionInfo } = await getDetectedExtension();

      // Always include extension info in the response if available
      const baseExtensionInfo = extensionInfo
        ? {
            name: extensionInfo.name,
            version: extensionInfo.version,
            allowlistState: {
              status: extensionInfo.result?.allowlist?.status || false,
              source: extensionInfo.result?.allowlist?.origin || null,
              oneCA: true,
              aa: extensionInfo.result?.aa,
              expiresAt: extensionInfo.result?.allowlist?.expiresAt,
            },
          }
        : null;

      // If premium bypass is not enabled or no license, return non-premium with extension info
      if (!this.premiumBypassModeEnabled || !extensionInfo?.result?.license) {
        return {
          isPremium: false,
          extensionInfo: baseExtensionInfo,
        };
      }

      // Validate license the same way as event payload
      const [encodedData, signature] = extensionInfo.result.license.split(".");
      const isValidPremium = await verifySignature({ encodedData, signature });

      if (!isValidPremium) {
        return {
          isPremium: false,
          extensionInfo: baseExtensionInfo,
        };
      }

      // Parse and validate premium status
      const { isPaying, expiresAt } = JSON.parse(decodeBase64(encodedData));
      const isPremium = isPaying && Date.now() < Number(expiresAt);

      return {
        isPremium,
        extensionInfo: baseExtensionInfo,
      };
    } catch (error) {
      console.error("Error in extension API premium detection:", error);
      return { isPremium: false, extensionInfo: null };
    }
  }

  /**
   * Legacy method for detecting premium users through window events
   * Used when extension API is not available
   */
  private detectPremiumABUserLegacy(): Promise<PremiumDetectionResult> {
    return new Promise<PremiumDetectionResult>((resolve) => {
      this.handlePreviousDetection(resolve);
    });
  }

  /**
   * Checks for previously cached detection data before attempting a new detection
   */
  private handlePreviousDetection(
    resolve: (value: PremiumDetectionResult) => void,
  ) {
    if (RL_PREVIEW || !this.RLEnabled) {
      // prevent fetch signature on preview
      resolve({ isPremium: false, extensionInfo: null });
      return;
    }
    const previousDetection = getPremiumDetectionData();
    if (previousDetection) {
      resolve(previousDetection);
      return;
    }
    this.fetchAndHandlePayload(resolve);
  }

  private async fetchAndHandlePayload(
    resolve: (value: PremiumDetectionResult) => void,
  ) {
    let isResolved = false;

    const safeResolve = (result: PremiumDetectionResult) => {
      if (!isResolved) {
        resolve(result);
        isResolved = true;
      }
    };

    try {
      const response = await fetch(
        `https://${apiDomain}/mw/sign_pbm?w=${websiteID}&bt_env=${bt_env}`,
      );
      const detail = await response.json();

      const timeoutId = setTimeout(() => {
        safeResolve({ isPremium: false, extensionInfo: null });
        document.removeEventListener("flattr-payload", handlePayloadEvent);
      }, 1000);

      const handlePayloadEvent = async (
        event: CustomEvent<{
          payload: string;
          extensionInfo: {
            name: string;
            version: string;
            allowlistState: {
              status: boolean;
              source: string | null;
              oneCA: boolean;
            };
          };
        }>,
      ) => {
        clearTimeout(timeoutId);
        document.removeEventListener("flattr-payload", handlePayloadEvent);

        let isPremium: PremiumDetectionResult["isPremium"] = false;
        let extensionInfo: PremiumDetectionResult["extensionInfo"] = null;

        if (
          this.premiumBypassModeEnabled &&
          typeof event.detail.payload === "string"
        ) {
          const [encodedData, signature] = event.detail.payload.split(".");
          try {
            const isValidPremium = await verifySignature({
              encodedData,
              signature,
            });
            if (isValidPremium) {
              const {
                isPaying,
                expiresAt,
              }: { isPaying: boolean; expiresAt: number } = JSON.parse(
                decodeBase64(encodedData),
              );
              if (isPaying && Date.now() < Number(expiresAt)) {
                isPremium = true;
              }
            }
          } catch (error) {
            console.error("Error verifying premium signature:", error);
          }
        }

        if (event.detail.extensionInfo) {
          extensionInfo = event.detail.extensionInfo;
        }

        safeResolve({ isPremium, extensionInfo });
      };

      document.addEventListener(
        "flattr-payload",
        handlePayloadEvent as EventListener,
      );

      const event = new CustomEvent("flattr-request-payload", {
        detail,
      });
      document.dispatchEvent(event);
    } catch {
      safeResolve({ isPremium: false, extensionInfo: null });
    }
  }
}

let premiumAbUserDetector: PremiumABUserDetector | null = null;

/**
 * Detects if a user has premium ad blocker status and gathers extension info
 * Uses a singleton pattern to prevent multiple detections
 *
 * @param settings - Local settings containing premium bypass mode config
 * @returns Promise resolving to premium status and extension information
 */
export async function detectPremiumABUser(
  settings: Settings,
): Promise<PremiumDetectionResult> {
  if (!premiumAbUserDetector) {
    premiumAbUserDetector = new PremiumABUserDetector(settings);
  }
  return premiumAbUserDetector.detectPremiumABUser();
}
